// View 1 — Tenants table.
//
// Per design/UI : UX v1.0/for-engineering-tenants/specs/tenants-strings.md.
// Layout: PageHeader → stat cards → filter row → table → footer count.
// Mirrors the Users page patterns 1:1 (chips, drawer-on-row-click,
// stat-card-as-chip, marketing empty state).
//
// V1 scope:
//   - Status chips + standing dropdown + jurisdiction dropdown + search
//   - Stat cards (TENANTS / ACTIVE / TRIAL / PAST DUE; click filters)
//   - "+ New tenant" → opens CreateTenantWizard (Super Admin only)
//   - Row click → opens TenantDetailDrawer
//   - Customer Success: read-only (no New button, drawer footer hidden)
//   - No bulk-actions bar in V1 (designer decision)

import { useMemo, useState, type ChangeEvent } from 'react';
import {
  Button,
  StatCard,
  TextInput,
} from '@gc-coach/design-system';
import type {
  TenantStanding,
  TenantStatusFilter,
  TenantSummary,
} from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { formatRelativeTime } from '../users/time.js';
import { CreateTenantWizard } from './CreateTenantWizard.js';
import { TenantDetailDrawer } from './TenantDetailDrawer.js';
import { useTenants } from './useTenants.js';
import { flagFromIso } from './flag.js';
import styles from './TenantsPage.module.css';

const STANDING_VALUES: ReadonlyArray<TenantStanding> = [
  'trial',
  'active',
  'past_due',
  'churned',
];

// V1: only EU-W1 enabled. Future regions appear grayed via the wizard, not
// in the table filter (no point filtering by a region that's locked).
//
// Jurisdiction filter: rather than ship a full ISO-3166 country picker for
// V1, derive the available options from what's actually in the result set.
// Phase 1 may want a curated allowlist.

export function TenantsPage() {
  const { t, tt } = useI18n();
  const { session } = useSession();
  const isSuperAdmin = session?.roles.includes('super_admin') ?? false;

  const [statusFilter, setStatusFilter] = useState<TenantStatusFilter>('all');
  const [standingFilter, setStandingFilter] = useState<TenantStanding | ''>('');
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string>('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(standingFilter ? { standing: standingFilter } : {}),
      ...(jurisdictionFilter ? { jurisdiction: jurisdictionFilter } : {}),
      ...(q.trim() ? { q } : {}),
    }),
    [statusFilter, standingFilter, jurisdictionFilter, q],
  );

  const { data, isLoading, refetch } = useTenants(query);
  // Memoize the tenants array so jurisdictionOptions' useMemo only recomputes
  // when the array contents actually change (data?.tenants ?? [] would
  // produce a new array identity on every render).
  const tenants = useMemo(() => data?.tenants ?? [], [data]);
  const counts = data?.counts ?? {
    total: 0,
    active: 0,
    suspended: 0,
    removed: 0,
    trial: 0,
    activeStanding: 0,
    pastDue: 0,
    churned: 0,
  };

  // Derive jurisdiction options from the current result set (V1 pragmatic
  // choice — see comment above).
  const jurisdictionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const tn of tenants) set.add(tn.jurisdiction);
    return Array.from(set).sort();
  }, [tenants]);

  // Marketing-style empty state only when truly zero tenants exist + no
  // filters are applied. Filtered-empty falls back to the regular
  // table-empty state (so the user knows their filter is restrictive).
  const showMarketingEmpty =
    !isLoading &&
    counts.total === 0 &&
    statusFilter === 'all' &&
    !standingFilter &&
    !jurisdictionFilter &&
    !q.trim();

  const subText = tt('tenants.subTemplate', {
    active: counts.active,
    suspended: counts.suspended,
    trial: counts.trial,
  });

  return (
    <AdminShell
      activeKey="tenants"
      breadcrumb={[t('tenants.breadcrumbCustomers'), t('tenants.breadcrumbTenants')]}
      state={isLoading && !data ? { kind: 'loading' } : { kind: 'normal' }}
    >
      <PageHeader
        title={t('tenants.title')}
        sub={subText}
        actions={
          isSuperAdmin ? (
            <Button kind="primary" onClick={() => setShowCreate(true)}>
              {t('tenants.newTenantButton')}
            </Button>
          ) : null
        }
      />

      {showMarketingEmpty ? (
        <div className={styles.emptyMarketing}>
          <h2>{t('tenants.emptyMarketingTitle')}</h2>
          <p>{t('tenants.emptyMarketingBody')}</p>
          {isSuperAdmin && (
            <Button kind="primary" onClick={() => setShowCreate(true)}>
              {t('tenants.newTenantButton')}
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className={styles.statRow}>
            <StatCard
              label={t('tenants.statTenants')}
              value={counts.total.toString()}
              onClick={() => setStatusFilter('all')}
            />
            <StatCard
              label={t('tenants.statActive')}
              value={counts.active.toString()}
              onClick={() => setStatusFilter('active')}
            />
            <StatCard
              label={t('tenants.statTrial')}
              value={counts.trial.toString()}
              onClick={() => setStandingFilter('trial')}
            />
            <StatCard
              label={t('tenants.statPastDue')}
              value={counts.pastDue.toString()}
              onClick={() => setStandingFilter('past_due')}
            />
          </div>

          {/* Filters */}
          <div className={styles.filterRow}>
            <div className={styles.chips} role="group" aria-label="Status filter">
              <FilterChip
                label={tt('tenants.chipAllTemplate', { n: counts.total - counts.removed })}
                active={statusFilter === 'all'}
                onClick={() => setStatusFilter('all')}
              />
              <FilterChip
                label={tt('tenants.chipActiveTemplate', { n: counts.active })}
                active={statusFilter === 'active'}
                onClick={() => setStatusFilter('active')}
              />
              <FilterChip
                label={tt('tenants.chipSuspendedTemplate', { n: counts.suspended })}
                active={statusFilter === 'suspended'}
                onClick={() => setStatusFilter('suspended')}
              />
              <FilterChip
                label={tt('tenants.chipRemovedTemplate', { n: counts.removed })}
                active={statusFilter === 'removed'}
                onClick={() => setStatusFilter('removed')}
              />
            </div>

            <div className={styles.filterRight}>
              <label className={styles.standingFilter}>
                <span className={styles.standingFilterLabel}>
                  {t('tenants.standingFilterLabel')}
                </span>
                <select
                  className={styles.roleSelect}
                  value={standingFilter}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setStandingFilter(e.target.value as TenantStanding | '')
                  }
                >
                  <option value="">{t('tenants.standingFilterAll')}</option>
                  {STANDING_VALUES.map((s) => (
                    <option key={s} value={s}>
                      {standingLabel(s, t)}
                    </option>
                  ))}
                </select>
              </label>

              {jurisdictionOptions.length > 0 && (
                <label className={styles.jurisdictionFilter}>
                  <span className={styles.jurisdictionFilterLabel}>
                    {t('tenants.jurisdictionFilterLabel')}
                  </span>
                  <select
                    className={styles.roleSelect}
                    value={jurisdictionFilter}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setJurisdictionFilter(e.target.value)
                    }
                  >
                    <option value="">{t('tenants.jurisdictionFilterAll')}</option>
                    {jurisdictionOptions.map((j) => (
                      <option key={j} value={j}>
                        {flagFromIso(j)} {j}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className={styles.search}>
                <TextInput
                  type="search"
                  placeholder={t('tenants.searchPlaceholder')}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label={t('tenants.searchPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('tenants.colTenant')}</th>
                  <th>{t('tenants.colJurisdiction')}</th>
                  <th>{t('tenants.colRegion')}</th>
                  <th>{t('tenants.colPlan')}</th>
                  <th>{t('tenants.colStanding')}</th>
                  <th>{t('tenants.colUsers')}</th>
                  <th>{t('tenants.colCreated')}</th>
                  <th>{t('tenants.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={8} className={styles.empty}>
                      <strong>{t('tenants.emptyTitle')}</strong>
                      <span>{t('tenants.emptyBody')}</span>
                    </td>
                  </tr>
                )}
                {tenants.map((tn) => (
                  <TenantRow
                    key={tn.id}
                    tenant={tn}
                    onClick={() => setSelectedId(tn.id)}
                  />
                ))}
              </tbody>
            </table>
            <footer className={styles.tableFooter}>
              {tt('tenants.footerCountTemplate', { n: tenants.length })}
            </footer>
          </div>
        </>
      )}

      {showCreate && (
        <CreateTenantWizard
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void refetch();
          }}
        />
      )}

      {selectedId && (
        <TenantDetailDrawer
          tenantId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            void refetch();
          }}
          onRemoved={() => {
            setSelectedId(null);
            void refetch();
          }}
        />
      )}
    </AdminShell>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function TenantRow({ tenant, onClick }: { tenant: TenantSummary; onClick: () => void }) {
  const { t } = useI18n();
  return (
    <tr className={styles.row} onClick={onClick} tabIndex={0}>
      <td>
        <div className={styles.cellTenant}>
          <span className={styles.tenantName}>{tenant.name}</span>
          <span className={styles.tenantSlug}>{tenant.slug}</span>
        </div>
      </td>
      <td className={styles.cellMuted}>
        <span className={styles.cellJurisdiction}>
          <span aria-hidden="true">{flagFromIso(tenant.jurisdiction)}</span>
          {tenant.jurisdiction}
        </span>
      </td>
      <td className={styles.cellMono}>{tenant.region.toUpperCase()}</td>
      <td className={styles.cellMuted}>—</td>
      <td>
        <StandingPill standing={tenant.standing} />
      </td>
      <td className={styles.cellMuted}>{tenant.userCount}</td>
      <td className={styles.cellMuted}>{formatRelativeTime(tenant.createdAt)}</td>
      <td className={statusClass(tenant.status, styles)}>{statusText(tenant.status, t)}</td>
    </tr>
  );
}

// ─── Standing pill ───────────────────────────────────────────────────────────

function StandingPill({ standing }: { standing: TenantStanding }) {
  const { t } = useI18n();
  const cls =
    standing === 'trial'
      ? styles.standingTrial
      : standing === 'active'
        ? styles.standingActive
        : standing === 'past_due'
          ? styles.standingPastDue
          : styles.standingChurned;
  return (
    <span className={`${styles.standingPill} ${cls ?? ''}`}>
      {standingLabel(standing, t)}
    </span>
  );
}

function standingLabel(s: TenantStanding, t: (k: string) => string): string {
  if (s === 'trial') return t('tenants.standingTrial');
  if (s === 'active') return t('tenants.standingActive');
  if (s === 'past_due') return t('tenants.standingPastDue');
  return t('tenants.standingChurned');
}

// ─── Status helpers ──────────────────────────────────────────────────────────

function statusClass(s: TenantSummary['status'], cls: Record<string, string>): string {
  if (s === 'active') return cls['statusActive'] ?? '';
  if (s === 'suspended') return cls['statusSuspended'] ?? '';
  return cls['statusRemoved'] ?? '';
}

function statusText(s: TenantSummary['status'], t: (k: string) => string): string {
  if (s === 'active') return t('tenants.statusActive');
  if (s === 'suspended') return t('tenants.statusSuspended');
  return t('tenants.statusRemoved');
}

// ─── Filter chip ─────────────────────────────────────────────────────────────

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[styles.chip, active ? styles.chipActive : null].filter(Boolean).join(' ')}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
